<template>
  <div v-if="isTouchDevice && showControls" id="touch-controls">
    <!-- Virtual Joystick for Movement -->
    <div 
      id="movement-joystick"
      @touchstart="onJoystickStart"
      @touchmove="onJoystickMove"
      @touchend="onJoystickEnd"
      @touchcancel="onJoystickEnd"
    >
      <div 
        id="joystick-handle"
        :style="{ 
          transform: `translate(${joystickPosition.x}px, ${joystickPosition.y}px)` 
        }"
      ></div>
    </div>
    
    <!-- Touch area for camera rotation (entire screen except buttons) -->
    <div 
      id="camera-touch-area"
      @touchstart="onCameraTouchStart"
      @touchmove="onCameraTouchMove"
      @touchend="onCameraTouchEnd"
      @touchcancel="onCameraTouchEnd"
    ></div>
    
    <!-- Shoot button -->
    <button 
      id="shoot-button"
      @touchstart="onShootStart"
      @touchend="onShootEnd"
      @touchcancel="onShootEnd"
    >
      🔫
    </button>
    
    <!-- Reload button -->
    <button 
      id="reload-button"
      @touchstart="onReloadStart"
      @touchend="onReloadEnd"
      @touchcancel="onReloadEnd"
    >
      🔄
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  showControls: { type: Boolean, default: false }
});

const emit = defineEmits(['move', 'camera-rotate', 'shoot', 'reload']);

const isTouchDevice = ref(false);
const joystickPosition = ref({ x: 0, y: 0 });
const joystickActive = ref(false);
const joystickCenter = ref({ x: 0, y: 0 });
const joystickRadius = 60; // Radius of joystick area

const cameraTouchActive = ref(false);
const lastCameraTouch = ref({ x: 0, y: 0 });

onMounted(() => {
  // Detect touch device
  isTouchDevice.value = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
});

// Joystick handlers
const onJoystickStart = (e) => {
  e.preventDefault();
  const touch = e.touches[0];
  const rect = e.currentTarget.getBoundingClientRect();
  joystickCenter.value = {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2
  };
  joystickActive.value = true;
  updateJoystickPosition(touch);
};

const onJoystickMove = (e) => {
  if (!joystickActive.value) return;
  e.preventDefault();
  const touch = e.touches[0];
  updateJoystickPosition(touch);
};

const onJoystickEnd = (e) => {
  e.preventDefault();
  joystickActive.value = false;
  joystickPosition.value = { x: 0, y: 0 };
  emit('move', { x: 0, y: 0 });
};

const updateJoystickPosition = (touch) => {
  const dx = touch.clientX - joystickCenter.value.x;
  const dy = touch.clientY - joystickCenter.value.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  if (distance > joystickRadius) {
    // Clamp to circle
    const angle = Math.atan2(dy, dx);
    joystickPosition.value = {
      x: Math.cos(angle) * joystickRadius,
      y: Math.sin(angle) * joystickRadius
    };
  } else {
    joystickPosition.value = { x: dx, y: dy };
  }
  
  // Normalize to -1 to 1 range
  const normalizedX = joystickPosition.value.x / joystickRadius;
  const normalizedY = joystickPosition.value.y / joystickRadius;
  
  emit('move', { x: normalizedX, y: normalizedY });
};

// Camera rotation handlers
const onCameraTouchStart = (e) => {
  // Don't interfere with joystick or buttons
  const target = e.target;
  if (target.id === 'movement-joystick' || 
      target.id === 'joystick-handle' ||
      target.id === 'shoot-button' ||
      target.id === 'reload-button' ||
      target.closest('#movement-joystick') ||
      target.closest('#shoot-button') ||
      target.closest('#reload-button')) {
    return;
  }
  
  e.preventDefault();
  e.stopPropagation();
  const touch = e.touches[0];
  if (!touch) return;
  
  lastCameraTouch.value = { x: touch.clientX, y: touch.clientY };
  cameraTouchActive.value = true;
};

const onCameraTouchMove = (e) => {
  if (!cameraTouchActive.value) return;
  
  // Don't interfere with joystick or buttons
  const target = e.target;
  if (target.id === 'movement-joystick' || 
      target.id === 'joystick-handle' ||
      target.id === 'shoot-button' ||
      target.id === 'reload-button' ||
      target.closest('#movement-joystick') ||
      target.closest('#shoot-button') ||
      target.closest('#reload-button')) {
    return;
  }
  
  e.preventDefault();
  e.stopPropagation();
  const touch = e.touches[0];
  if (!touch) return;
  
  const dx = touch.clientX - lastCameraTouch.value.x;
  const dy = touch.clientY - lastCameraTouch.value.y;
  
  // Only emit if there's significant movement
  if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
    emit('camera-rotate', { 
      deltaX: dx,
      deltaY: dy 
    });
  }
  
  lastCameraTouch.value = { x: touch.clientX, y: touch.clientY };
};

const onCameraTouchEnd = (e) => {
  e.preventDefault();
  e.stopPropagation();
  cameraTouchActive.value = false;
};

// Shoot button handlers
const onShootStart = (e) => {
  e.preventDefault();
  emit('shoot', true);
};

const onShootEnd = (e) => {
  e.preventDefault();
  emit('shoot', false);
};

// Reload button handlers
const onReloadStart = (e) => {
  e.preventDefault();
  emit('reload');
};

const onReloadEnd = (e) => {
  e.preventDefault();
};
</script>

<style scoped>
#touch-controls {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 150;
  touch-action: none;
}

/* Movement Joystick (left side) */
#movement-joystick {
  position: absolute;
  bottom: 120px;
  left: 50px;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  border: 3px solid rgba(255, 255, 255, 0.3);
  pointer-events: auto;
  touch-action: none;
  z-index: 160;
}

#joystick-handle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 50px;
  height: 50px;
  margin: -25px 0 0 -25px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.8);
  border: 2px solid rgba(255, 255, 255, 0.9);
  transition: transform 0.1s ease-out;
  pointer-events: none;
}

/* Camera Touch Area (entire screen, but buttons have higher z-index) */
#camera-touch-area {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: auto;
  touch-action: none;
  z-index: 140;
  background: transparent;
  /* Exclude joystick area (left bottom) and button areas (right bottom) */
  /* These are handled by their own elements with higher z-index */
}

/* Shoot Button (right bottom) */
#shoot-button {
  position: absolute;
  bottom: 120px;
  right: 50px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(200, 0, 0, 0.8);
  border: 3px solid rgba(255, 255, 255, 0.5);
  color: white;
  font-size: 2em;
  pointer-events: auto;
  touch-action: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  transition: all 0.1s;
  z-index: 160;
}

#shoot-button:active {
  background: rgba(255, 0, 0, 0.9);
  transform: scale(0.95);
}

/* Reload Button (right bottom, above shoot) */
#reload-button {
  position: absolute;
  bottom: 220px;
  right: 50px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(0, 100, 200, 0.8);
  border: 3px solid rgba(255, 255, 255, 0.5);
  color: white;
  font-size: 1.5em;
  pointer-events: auto;
  touch-action: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  transition: all 0.1s;
  z-index: 160;
}

#reload-button:active {
  background: rgba(0, 150, 255, 0.9);
  transform: scale(0.95);
}

/* Tablet specific adjustments */
@media (min-width: 768px) and (max-width: 1024px) {
  #movement-joystick {
    bottom: 150px;
    left: 80px;
    width: 140px;
    height: 140px;
  }
  
  #joystick-handle {
    width: 60px;
    height: 60px;
    margin: -30px 0 0 -30px;
  }
  
  #shoot-button {
    bottom: 150px;
    right: 80px;
    width: 100px;
    height: 100px;
    font-size: 2.5em;
  }
  
  #reload-button {
    bottom: 270px;
    right: 80px;
    width: 80px;
    height: 80px;
    font-size: 2em;
  }
}
</style>

